// ── Tile Type Classification ──
// Every walkable tile is classified into exactly one of 11 logical path types
// based on its connections to neighbouring walkable tiles.
//
// Direction bitmask: U=1 D=2 L=4 R=8
//
// Type           Connections    Mask
// ─────────────  ─────────────  ────
// H  horizontal  L + R          12
// V  vertical    U + D           3
// C_TL corner    D + R          10
// C_TR corner    D + L           6
// C_BL corner    U + R           9
// C_BR corner    U + L           5
// T_UP           U + L + R      13
// T_DOWN         D + L + R      14
// T_LEFT         U + D + L       7
// T_RIGHT        U + D + R      11
// CROSS          U + D + L + R  15

import { GRID_COLS, GRID_ROWS, CELL, TUNNEL_ROW } from './config';

// Direction bitmask constants
export const MASK = { U: 1, D: 2, L: 4, R: 8 };

// Tile type identifiers
export const TILE_TYPE = {
  WALL:    'wall',
  HUB:     'hub',
  H:       'h',
  V:       'v',
  C_TL:    'c_tl',
  C_TR:    'c_tr',
  C_BL:    'c_bl',
  C_BR:    'c_br',
  T_UP:    't_up',
  T_DOWN:  't_down',
  T_LEFT:  't_left',
  T_RIGHT: 't_right',
  CROSS:   'cross',
};

// Valid path bitmasks (exactly the 11 allowed types — all have ≥ 2 connections)
export const VALID_PATH_MASKS = new Set([3, 5, 6, 7, 9, 10, 11, 12, 13, 14, 15]);

// Bitmask → tile type name
const MASK_TO_TYPE = {
  3:  TILE_TYPE.V,
  5:  TILE_TYPE.C_BR,
  6:  TILE_TYPE.C_TR,
  7:  TILE_TYPE.T_LEFT,
  9:  TILE_TYPE.C_BL,
  10: TILE_TYPE.C_TL,
  11: TILE_TYPE.T_RIGHT,
  12: TILE_TYPE.H,
  13: TILE_TYPE.T_UP,
  14: TILE_TYPE.T_DOWN,
  15: TILE_TYPE.CROSS,
};

function isWalkable(grid, c, r) {
  if (c < 0 || c >= GRID_COLS || r < 0 || r >= GRID_ROWS) return false;
  const t = grid[r][c];
  return t === CELL.CORRIDOR || t === CELL.POWERUP || t === CELL.TUNNEL;
}

/** Compute the 4-bit connection mask for a walkable tile */
export function getConnectionMask(grid, c, r) {
  if (!isWalkable(grid, c, r)) return 0;

  let mask = 0;
  if (isWalkable(grid, c, r - 1)) mask |= MASK.U;
  if (isWalkable(grid, c, r + 1)) mask |= MASK.D;
  if (isWalkable(grid, c - 1, r)) mask |= MASK.L;
  if (isWalkable(grid, c + 1, r)) mask |= MASK.R;

  // Tunnel warp: endpoints connect to the opposite side
  if (r === TUNNEL_ROW) {
    if (c === 0 && isWalkable(grid, GRID_COLS - 1, r)) mask |= MASK.L;
    if (c === GRID_COLS - 1 && isWalkable(grid, 0, r))  mask |= MASK.R;
  }
  // Vertical tunnel warp
  if (r === 0 && grid[0][c] === CELL.TUNNEL && isWalkable(grid, c, GRID_ROWS - 1)) mask |= MASK.U;
  if (r === GRID_ROWS - 1 && grid[GRID_ROWS - 1][c] === CELL.TUNNEL && isWalkable(grid, c, 0)) mask |= MASK.D;

  return mask;
}

/** Classify a single tile → TILE_TYPE string */
export function classifyTile(grid, c, r) {
  const cell = grid[r][c];
  if (cell === CELL.HUB) return TILE_TYPE.HUB;
  if (!isWalkable(grid, c, r)) return TILE_TYPE.WALL;
  const mask = getConnectionMask(grid, c, r);
  return MASK_TO_TYPE[mask] || null;
}

/** Classify the entire grid → { types: string[][], masks: number[][] } */
export function classifyAllTiles(grid) {
  const types = [];
  const masks = [];
  for (let r = 0; r < GRID_ROWS; r++) {
    types[r] = [];
    masks[r] = [];
    for (let c = 0; c < GRID_COLS; c++) {
      const cell = grid[r][c];
      if (cell === CELL.HUB) {
        types[r][c] = TILE_TYPE.HUB;
        masks[r][c] = 0;
      } else if (!isWalkable(grid, c, r)) {
        types[r][c] = TILE_TYPE.WALL;
        masks[r][c] = 0;
      } else {
        const m = getConnectionMask(grid, c, r);
        types[r][c] = MASK_TO_TYPE[m] || null;
        masks[r][c] = m;
      }
    }
  }
  return { types, masks };
}

/** Validate: every walkable tile must be one of the 11 valid path types */
export function validateTileTypes(grid, idx) {
  for (let r = 0; r < GRID_ROWS; r++) {
    for (let c = 0; c < GRID_COLS; c++) {
      if (!isWalkable(grid, c, r)) continue;
      // Tunnel endpoints have warp — still validated via getConnectionMask
      const mask = getConnectionMask(grid, c, r);
      if (!VALID_PATH_MASKS.has(mask)) {
        console.warn(`T${idx}: invalid tile at (${c},${r}) mask=${mask}`);
        return false;
      }
    }
  }
  return true;
}
